import { FormItem, ItemOption, ItemType } from "../../types";

export type OptionIssueKind = "duplicate" | "blank";

export interface OptionIssue {
  optionId: string;
  kind: OptionIssueKind;
}

// Cleared number inputs arrive as NaN (parseInt("")); the API returns null.
function isBlank(value: number | null | undefined) {
  return value === null || value === undefined || Number.isNaN(value);
}

function sortedOptions(formItem: FormItem): ItemOption[] {
  return [...(formItem.options ?? [])].sort((a, b) => a.order - b.order);
}

export function getOptionIssues(formItem: FormItem): OptionIssue[] {
  if (formItem.itemType !== ItemType.Choice) {
    return [];
  }
  const options = sortedOptions(formItem);
  return options.flatMap((option): OptionIssue[] => {
    if (isBlank(option.value)) {
      return [{ optionId: option.id, kind: "blank" }];
    }
    const duplicated = options.some(
      (x) => x.id !== option.id && x.value === option.value,
    );
    return duplicated ? [{ optionId: option.id, kind: "duplicate" }] : [];
  });
}

// One lowercase fragment per issue kind present, for composing messages in
// different contexts (editor tooltip, collapsed-question flag).
export function optionProblemSummaries(issues: OptionIssue[]): string[] {
  return [
    issues.some((x) => x.kind === "blank") && "every option needs a value",
    issues.some((x) => x.kind === "duplicate") &&
      "every option needs a unique value",
  ].filter(Boolean) as string[];
}

// Explains why "Finish Editing" is disabled; empty when there's nothing to fix.
export function describeOptionIssues(issues: OptionIssue[]): string {
  const problems = optionProblemSummaries(issues);
  if (problems.length === 0) {
    return "";
  }
  const sentence = problems.join(", and ");
  return `Please fix the highlighted fields. ${
    sentence.charAt(0).toUpperCase() + sentence.slice(1)
  }.`;
}
